import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Check,
  CircleAlert,
  ChevronRight,
  Database,
  FileCheck2,
  Handshake,
  Headphones,
  KeyRound,
  Landmark,
  Layers3,
  LocateFixed,
  Mail,
  MapPinned,
  Menu,
  ReceiptText,
  Route,
  ServerCog,
  ShieldCheck,
  Smartphone,
  X,
} from 'lucide-react';
import {
  fetchPragueZones,
  findZoneAtPoint,
  type ParkingZoneCollection,
  type ParkingZoneFeature,
} from './prague-zones';
import {
  getAnalyticsConsent,
  initializeAnalytics,
  isAnalyticsConfigured,
  setAnalyticsConsent,
  trackPageView,
  trackPublicAction,
  type AnalyticsConsent,
} from './analytics';
import {
  getPageMetadata,
  PUBLIC_ERROR_METADATA,
  routeFromPath,
  type RouteName,
} from './public-content';
const CityOpsPage = lazy(() =>
  import('./city-ops').then((module) => ({ default: module.CityOpsPage })),
);
const ParkingMap = lazy(() => import('./parking-map'));
import {
  detectPublicLocale,
  localeSwitchUrl,
  PUBLIC_ORIGINS,
  PUBLIC_LOCALES,
  PUBLIC_OG_LOCALES,
  PUBLISHED_PUBLIC_LOCALES,
  publicManifestPath,
  isLocalPublicHostname,
  publicPath,
  type PublicLocale,
} from './locale';
import { translatePublic, PUBLIC_LANGUAGE_NAMES } from './public-copy';
import './styles.css';

type ZoneState = 'idle' | 'loading' | 'ready' | 'outside' | 'error';

const CONTACT_EMAIL = 'yellowbenchprague@gmail.com';
const OPERATOR_NAME = 'Yellow Bench s.r.o.';
const OPERATOR_ID = '07601662';
const OPERATOR_ADDRESS = 'Václavské náměstí 819/43, Nové Město, 110 00 Praha 1';
const DEFAULT_LOCATION = { latitude: 50.0755, longitude: 14.4378 };
const LocaleContext = createContext<PublicLocale>('cs');

function usePublicLocale() {
  const locale = useContext(LocaleContext);
  const text = useCallback(
    (cs: string, sk: string) => translatePublic(locale, cs, sk),
    [locale],
  );
  return {
    locale,
    sk: locale === 'sk',
    text,
  };
}

function navigate(path: string) {
  if (isLocalPublicHostname(window.location.hostname)) {
    const language = new URLSearchParams(window.location.search).get('lang');
    if (language && !path.includes('?')) path += `?lang=${language}`;
  }
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function Brand({ compact = false }: { compact?: boolean }) {
  const { text } = usePublicLocale();
  return (
    <a
      className={`brand ${compact ? 'brand--compact' : ''}`}
      href="/"
      onClick={(event) => {
        event.preventDefault();
        navigate('/');
      }}
      aria-label={text('Parknuto — úvod', 'Parknuto — úvodná stránka')}
    >
      par<span>k</span>nuto
    </a>
  );
}

function Header({ route }: { route: RouteName }) {
  const [open, setOpen] = useState(false);
  const { locale, text } = usePublicLocale();
  const links = [
    {
      route: 'app' as const,
      path: publicPath('app', locale),
      label: text('Mapa zón', 'Mapa zón'),
    },
    {
      route: 'coverage' as const,
      path: publicPath('coverage', locale),
      label: text('Pokrytí', 'Pokrytie'),
    },
    {
      route: 'partners' as const,
      path: publicPath('partners', locale),
      label: text('Pro města', 'Pre mestá'),
    },
  ];
  return (
    <header className="site-header">
      <div className="site-header__inner">
        <Brand compact />
        <nav
          className={`site-nav ${open ? 'site-nav--open' : ''}`}
          aria-label={text('Hlavní navigace', 'Hlavná navigácia')}
        >
          {links.map((link) => (
            <a
              key={link.route}
              href={link.path}
              className={route === link.route ? 'is-active' : ''}
              onClick={(event) => {
                event.preventDefault();
                setOpen(false);
                navigate(link.path);
              }}
            >
              {link.label}
            </a>
          ))}
          <label className="locale-switch">
            <span className="sr-only">{text('Jazyk', 'Jazyk')}</span>
            <select
              value={locale}
              onChange={(event) => {
                window.location.assign(
                  localeSwitchUrl(
                    event.target.value as PublicLocale,
                    route === 'cityops' || route === 'notfound' ? 'app' : route,
                    window.location.hostname,
                  ),
                );
              }}
            >
              {PUBLISHED_PUBLIC_LOCALES.map((targetLocale) => (
                <option
                  key={targetLocale}
                  value={targetLocale}
                  lang={targetLocale}
                >
                  {PUBLIC_LANGUAGE_NAMES[targetLocale]}
                </option>
              ))}
            </select>
          </label>
          <a className="nav-contact" href={`mailto:${CONTACT_EMAIL}`}>
            {text('Kontakt', 'Kontakt')}
          </a>
        </nav>
        <button
          className="menu-button"
          type="button"
          aria-label={
            open
              ? text('Zavřít menu', 'Zavrieť menu')
              : text('Otevřít menu', 'Otvoriť menu')
          }
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
    </header>
  );
}

function PilotStrip() {
  const { text } = usePublicLocale();
  return (
    <div className="pilot-strip" role="status">
      <span className="pilot-dot" />
      {text(
        'Ukázka aplikace · živá data Prahy, platby zatím nejsou aktivní',
        'Ukážka aplikácie · živé dáta Prahy, platby zatiaľ nie sú aktívne',
      )}
    </div>
  );
}

function LocationButton({
  onLocate,
  locating,
}: {
  onLocate: () => void;
  locating: boolean;
}) {
  const { text } = usePublicLocale();
  return (
    <button
      className="map-location-button"
      type="button"
      onClick={onLocate}
      aria-label={
        locating
          ? text('Hledám vaši polohu', 'Hľadám vašu polohu')
          : text('Použít moji polohu', 'Použiť moju polohu')
      }
      disabled={locating}
    >
      {locating ? <span className="button-spinner" /> : <LocateFixed />}
      <span>
        {locating
          ? text('Hledám…', 'Hľadám…')
          : text('Moje poloha', 'Moja poloha')}
      </span>
    </button>
  );
}

function ParkingPanel({
  zoneState,
  selectedZone,
  error,
  pointOrigin,
  onRetry,
}: {
  zoneState: ZoneState;
  selectedZone: ParkingZoneFeature | null;
  error: string | null;
  pointOrigin: 'example' | 'selected' | 'device';
  onRetry: () => void;
}) {
  const { locale, text } = usePublicLocale();
  const interestHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(text('Zájem o spuštění Parknuto', 'Záujem o spustenie Parknuto'))}&body=${encodeURIComponent(text('Dobrý den, mám zájem o zprávu při spuštění Parknuto.\nMěsto, ve kterém parkuji: ', 'Dobrý deň, mám záujem o správu pri spustení Parknuto.\nMesto, v ktorom parkujem: '))}`;
  return (
    <section className="parking-panel" aria-labelledby="parking-panel-title">
      <div className="sheet-handle" aria-hidden="true" />
      <div className="panel-heading">
        <div>
          <span className="eyebrow">
            {text('Ukázka aplikace', 'Ukážka aplikácie')}
          </span>
          <h1 id="parking-panel-title">
            {text('Najděte zónu v Praze.', 'Nájdite zónu v Prahe.')}
          </h1>
        </div>
        <span className="live-chip">
          <span />
          {text('Živá data', 'Živé dáta')}
        </span>
      </div>
      <p className="panel-intro">
        {text(
          'Platby přes Parknuto zatím nejsou dostupné. Zónu si můžete prohlédnout bez registrace a SPZ.',
          'Platby cez Parknuto zatiaľ nie sú dostupné. Zónu si môžete prezrieť bez registrácie a EČV.',
        )}
      </p>
      <p className="point-origin">
        {pointOrigin === 'example'
          ? text(
              'Ukázkový bod v Praze · nejde o vaši polohu',
              'Ukážkový bod v Prahe · nejde o vašu polohu',
            )
          : pointOrigin === 'device'
            ? text(
                'Bod podle polohy zařízení · ověřte zónu podle značení',
                'Bod podľa polohy zariadenia · overte zónu podľa značenia',
              )
            : text(
                'Bod vybraný v mapě · ověřte zónu podle značení',
                'Bod vybraný v mape · overte zónu podľa značenia',
              )}
      </p>
      <div
        className={`zone-card zone-card--${zoneState}`}
        role="status"
        aria-live="polite"
      >
        <div className="zone-card__icon">
          {zoneState === 'loading' ? (
            <span className="button-spinner dark" />
          ) : (
            <MapPinned />
          )}
        </div>
        <div className="zone-card__copy">
          {zoneState === 'loading' && (
            <>
              <strong>
                {text('Ověřuji parkovací zónu', 'Overujem parkovaciu zónu')}
              </strong>
              <span>
                {text(
                  'Načítám aktuální data města.',
                  'Načítavam aktuálne dáta mesta.',
                )}
              </span>
            </>
          )}
          {zoneState === 'ready' && selectedZone && (
            <>
              <strong>{selectedZone.properties.code}</strong>
              <span>
                {(locale === 'cs' && selectedZone.properties.tariftext) ||
                  text(
                    'Tarif ověřte na dopravním značení',
                    'Tarifu si overte na dopravnom značení',
                  )}
              </span>
              <small>
                {selectedZone.properties.poskyt ||
                  text(
                    'datový podklad © IPR Praha',
                    'dátový podklad © IPR Praha',
                  )}
              </small>
            </>
          )}
          {zoneState === 'outside' && (
            <>
              <strong>
                {text('Mimo podporovanou zónu', 'Mimo podporovanej zóny')}
              </strong>
              <span>
                {text(
                  'Klepněte do mapy na placenou zónu v Praze.',
                  'Ťuknite do mapy na spoplatnenú zónu v Prahe.',
                )}
              </span>
            </>
          )}
          {zoneState === 'error' && (
            <>
              <strong>
                {text('Zónu se nepodařilo ověřit', 'Zónu sa nepodarilo overiť')}
              </strong>
              <span>
                {error ||
                  text(
                    'Zkuste to znovu za chvíli.',
                    'Skúste to znova o chvíľu.',
                  )}
              </span>
            </>
          )}
          {zoneState === 'idle' && (
            <>
              <strong>
                {text('Ukázkový bod v Praze', 'Ukážkový bod v Prahe')}
              </strong>
              <span>
                {text(
                  'Povolte polohu nebo klepněte do mapy.',
                  'Povoľte polohu alebo ťuknite do mapy.',
                )}
              </span>
            </>
          )}
        </div>
        {zoneState === 'ready' && <BadgeCheck className="zone-check" />}
      </div>

      {zoneState === 'error' && (
        <button className="text-button" type="button" onClick={onRetry}>
          {text('Zkusit načíst zónu znovu', 'Skúsiť načítať zónu znova')}
        </button>
      )}
      <p className="field-help">
        {text(
          'Pro jiné místo klepněte do mapy nebo použijte tlačítko polohy. Tarif i platnost zóny vždy ověřte na místním značení.',
          'Pre iné miesto ťuknite do mapy alebo použite tlačidlo polohy. Tarifu aj platnosť zóny vždy overte na miestnom značení.',
        )}
      </p>
      <a
        className="primary-button"
        href="https://platba.parkujvpraze.cz/"
        onClick={() =>
          trackPublicAction('official_payment_link_clicked', locale)
        }
      >
        {text(
          'Otevřít oficiální platbu Praha',
          'Otvoriť oficiálnu platbu za parkovanie v Prahe',
        )}
        <ArrowRight aria-hidden="true" />
      </a>
      <p className="safety-note">
        <ShieldCheck aria-hidden="true" />
        {text(
          'Pokračujete na Online parkovací automat města. Parknuto žádnou platbu ani lístek nevytváří.',
          'Pokračujete na Online parkovací automat mesta. Parknuto žiadnu platbu ani lístok nevytvára.',
        )}
      </p>
      <a
        className="official-alternatives"
        href="https://parking.praha.eu/cs/platba-za-parkovani/"
      >
        {text(
          'Další platební možnosti na webu města',
          'Ďalšie platobné možnosti na webe mesta',
        )}
      </a>
      <div className="launch-interest" id="zajem">
        <h2>{text('Chcete vědět o spuštění?', 'Chcete vedieť o spustení?')}</h2>
        <p>
          {text(
            'Napište nám, ve kterém městě parkujete.',
            'Napíšte nám, v ktorom meste parkujete.',
          )}
        </p>
        <a
          className="text-button"
          href={interestHref}
          onClick={() => trackPublicAction('launch_interest_clicked', locale)}
        >
          {text('Napsat zájem o spuštění', 'Mám záujem o spustenie')}
          <Mail aria-hidden="true" />
        </a>
        <small>
          {text(
            'Otevře se e-mailová aplikace. Zájem potvrdíte až odesláním zprávy.',
            'Otvorí sa e-mailová aplikácia. Záujem potvrdíte až odoslaním správy.',
          )}
        </small>
      </div>
    </section>
  );
}

function AppPage() {
  const { locale, text } = usePublicLocale();
  const [position, setPosition] = useState(DEFAULT_LOCATION);
  const [pointOrigin, setPointOrigin] = useState<
    'example' | 'selected' | 'device'
  >('example');
  const [mapReady, setMapReady] = useState(false);
  const [collection, setCollection] = useState<ParkingZoneCollection | null>(
    null,
  );
  const [selectedZone, setSelectedZone] = useState<ParkingZoneFeature | null>(
    null,
  );
  const [zoneState, setZoneState] = useState<ZoneState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const requestRef = useRef<AbortController | null>(null);

  const loadPosition = useCallback(
    async (latitude: number, longitude: number) => {
      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;
      setPosition({ latitude, longitude });
      setZoneState('loading');
      setError(null);
      try {
        const zones = await fetchPragueZones(
          latitude,
          longitude,
          controller.signal,
        );
        const match = findZoneAtPoint(zones.features, latitude, longitude);
        setCollection(zones);
        setSelectedZone(match);
        setZoneState(match ? 'ready' : 'outside');
      } catch {
        if (controller.signal.aborted) return;
        setCollection(null);
        setSelectedZone(null);
        setError(
          text('Zónová data nejsou dostupná.', 'Zónové dáta nie sú dostupné.'),
        );
        setZoneState('error');
      }
    },
    [text],
  );

  useEffect(() => {
    setMapReady(true);
    void loadPosition(DEFAULT_LOCATION.latitude, DEFAULT_LOCATION.longitude);
    return () => requestRef.current?.abort();
  }, [loadPosition]);

  const locate = () => {
    if (!navigator.geolocation) {
      setError(
        text(
          'Tento prohlížeč nepodporuje zjištění polohy.',
          'Tento prehliadač nepodporuje zisťovanie polohy.',
        ),
      );
      setZoneState('error');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (result) => {
        setLocating(false);
        setPointOrigin('device');
        void loadPosition(result.coords.latitude, result.coords.longitude);
      },
      () => {
        setLocating(false);
        setError(
          text(
            'Poloha nebyla povolena. Zvolte místo klepnutím do mapy.',
            'Poloha nebola povolená. Vyberte miesto ťuknutím do mapy.',
          ),
        );
        setZoneState('error');
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
    );
  };

  return (
    <>
      <PilotStrip />
      <main className="app-main">
        <div className="map-stage">
          {mapReady && (
            <Suspense
              fallback={
                <div className="map-placeholder">
                  {text('Načítám mapu…', 'Načítavam mapu…')}
                </div>
              }
            >
              <ParkingMap
                collection={collection}
                position={position}
                selectedZone={selectedZone}
                pointLabel={
                  pointOrigin === 'example'
                    ? text('Ukázkový bod v Praze', 'Ukážkový bod v Prahe')
                    : text('Vybraný bod', 'Vybraný bod')
                }
                locale={locale}
                onPositionChange={(latitude, longitude) => {
                  setPointOrigin('selected');
                  void loadPosition(latitude, longitude);
                }}
              />
            </Suspense>
          )}
          <div className="map-brand">
            <Brand compact />
          </div>
          <LocationButton onLocate={locate} locating={locating} />
          <div className="map-source">
            {text('datový podklad © IPR Praha', 'dátový podklad © IPR Praha')} ·{' '}
            {text(
              'zónu vždy ověřte podle značení',
              'zónu si vždy overte podľa značenia',
            )}
          </div>
        </div>
        <ParkingPanel
          zoneState={zoneState}
          selectedZone={selectedZone}
          error={error}
          pointOrigin={pointOrigin}
          onRetry={() =>
            void loadPosition(position.latitude, position.longitude)
          }
        />
      </main>
      <section className="driver-story page-shell">
        <div className="section-heading">
          <span className="eyebrow">
            {text('Jedna aplikace', 'Jedna aplikácia')}
          </span>
          <h2>
            {text(
              'Parkování, kterému rozumíte.',
              'Parkovanie, ktorému rozumiete.',
            )}
          </h2>
          <p>
            {text(
              'Parknuto stavíme jako běžnou parkovací aplikaci: jasná zóna, SPZ, limit a potvrzení města před zobrazením aktivního parkování.',
              'Parknuto tvoríme ako bežnú parkovaciu aplikáciu: jasná zóna, EČV, limit a potvrdenie mesta pred zobrazením aktívneho parkovania.',
            )}
          </p>
        </div>
        <div className="feature-grid">
          <FeatureCard
            icon={<MapPinned />}
            title={text('Správná zóna', 'Správna zóna')}
            text={text(
              'Poloha i ruční výběr. Zónu před platbou vždy znovu potvrdíte.',
              'Poloha aj ručný výber. Zónu pred platbou vždy znovu potvrdíte.',
            )}
          />
          <FeatureCard
            icon={<ReceiptText />}
            title={text('Cena předem', 'Cena vopred')}
            text={text(
              'Městské parkovné, případný poplatek a maximální částka odděleně.',
              'Mestské parkovné, prípadný poplatok a maximálna suma oddelene.',
            )}
          />
          <FeatureCard
            icon={<ShieldCheck />}
            title={text('Bezpečný stav', 'Bezpečný stav')}
            text={text(
              'Parkování ukážeme jako aktivní až po potvrzení městského systému.',
              'Parkovanie zobrazíme ako aktívne až po potvrdení mestského systému.',
            )}
          />
        </div>
      </section>
      <section className="partner-teaser">
        <div className="page-shell partner-teaser__inner">
          <div>
            <span className="eyebrow">
              {text('Města a provozovatelé', 'Mestá a prevádzkovatelia')}
            </span>
            <h2>
              {text(
                'Bezpečný nový kanál pro placení parkování.',
                'Bezpečný nový kanál na platenie parkovania.',
              )}
            </h2>
            <p>
              {text(
                'Máme připravený technický a provozní rámec pro sandbox, certifikaci, tarifní data, settlement i podporu.',
                'Máme pripravený technický a prevádzkový rámec pre sandbox, certifikáciu, tarifné dáta, zúčtovanie aj podporu.',
              )}
            </p>
          </div>
          <button
            className="secondary-button"
            onClick={() => navigate(publicPath('partners', locale))}
          >
            {text('Podklady pro napojení', 'Podklady na pripojenie')}{' '}
            <ArrowRight />
          </button>
        </div>
      </section>
    </>
  );
}

function FeatureCard({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <article className="feature-card">
      <span>{icon}</span>
      <h3>{title}</h3>
      <p>{text}</p>
    </article>
  );
}

function integrationNeeds(text: (cs: string, sk: string) => string) {
  return [
    {
      icon: <Layers3 />,
      title: text('Technické rozhraní', 'Technické rozhranie'),
      items: [
        text('Sandbox a autentizace', 'Sandbox a autentifikácia'),
        text('Zóny a verzovaný tarifní feed', 'Zóny a verziovaný tarifný feed'),
        text(
          'Start, prodloužení, stop a potvrzení',
          'Štart, predĺženie, ukončenie a potvrdenie',
        ),
        text(
          'Idempotence, limity a change policy',
          'Idempotencia, limity a pravidlá zmien',
        ),
      ],
    },
    {
      icon: <Handshake />,
      title: text('Smlouva a finance', 'Zmluva a financie'),
      items: [
        text('Rozsah zón a oprávnění', 'Rozsah zón a oprávnení'),
        text(
          'Cenový model a schválené poplatky',
          'Cenový model a schválené poplatky',
        ),
        text(
          'Settlement, DPH, refundy a reklamace',
          'Zúčtovanie, DPH, refundácie a reklamácie',
        ),
        text(
          'Certifikace, jistota a pojištění',
          'Certifikácia, zábezpeka a poistenie',
        ),
      ],
    },
    {
      icon: <ShieldCheck />,
      title: text('Bezpečnost a provoz', 'Bezpečnosť a prevádzka'),
      items: [
        text(
          'DPA, retence a EU data residency',
          'DPA, retencia a uloženie dát v EÚ',
        ),
        text(
          'SLA, RPO/RTO a incidentní eskalace',
          'SLA, RPO/RTO a eskalácia incidentov',
        ),
        text(
          'Auditní log a reconciliation',
          'Auditný log a párovanie transakcií',
        ),
        text(
          'Monitoring, support a change notification',
          'Monitoring, podpora a oznamovanie zmien',
        ),
      ],
    },
  ];
}

function PartnersPage() {
  const { text } = usePublicLocale();
  return (
    <main className="content-page">
      <section className="partner-hero page-shell">
        <div className="partner-hero__copy">
          <span className="eyebrow">
            {text(
              'Pro města, správce a technologické partnery',
              'Pre mestá, správcov a technologických partnerov',
            )}
          </span>
          <h1>
            {text(
              'Nový distribuční kanál. Stejná pravidla města.',
              'Nový distribučný kanál. Rovnaké pravidlá mesta.',
            )}
          </h1>
          <p>
            {text(
              'Parknuto připravuje nativní mobilní aplikaci pro úhradu městského parkování. Integraci vedeme město po městě — smluvně, technicky i provozně.',
              'Parknuto pripravuje natívnu mobilnú aplikáciu na úhradu mestského parkovania. Integráciu riešime mesto po meste — zmluvne, technicky aj prevádzkovo.',
            )}
          </p>
          <div className="hero-actions">
            <a
              className="primary-button"
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
                text(
                  'Napojení Parknuto — úvodní jednání',
                  'Pripojenie Parknuto — úvodné rokovanie',
                ),
              )}&body=${encodeURIComponent(
                text(
                  'Dobrý den,\n\nmáme zájem projednat napojení Parknuto pro:\nOrganizace:\nMěsto / lokality:\nKontaktní osoba:\n\nDěkuji.',
                  'Dobrý deň,\n\nmáme záujem prerokovať pripojenie Parknuto pre:\nOrganizácia:\nMesto / lokality:\nKontaktná osoba:\n\nĎakujem.',
                ),
              )}`}
            >
              {text('Domluvit úvodní jednání', 'Dohodnúť úvodné rokovanie')}{' '}
              <Mail />
            </a>
            <a className="text-button" href="#podklady">
              {text(
                'Co potřebujeme k napojení',
                'Čo potrebujeme na pripojenie',
              )}{' '}
              <ChevronRight />
            </a>
          </div>
        </div>
        <div className="partner-hero__card">
          <div className="architecture-mark">
            <Smartphone />
            <span />
            <ServerCog />
            <span />
            <Landmark />
          </div>
          <h2>
            {text('Integrační hranice Parknuto', 'Integračné hranice Parknuto')}
          </h2>
          <dl>
            <div>
              <dt>{text('Klient', 'Klient')}</dt>
              <dd>
                {text(
                  'Nativní iOS a Android · vícejazyčný web',
                  'Natívne aplikácie pre iOS a Android · viacjazyčný web',
                )}
              </dd>
            </div>
            <div>
              <dt>{text('Platforma', 'Platforma')}</dt>
              <dd>
                {text(
                  'EU runtime · API + worker',
                  'Infraštruktúra v EÚ · API + worker',
                )}
              </dd>
            </div>
            <div>
              <dt>{text('Adaptér města', 'Adaptér mesta')}</dt>
              <dd>
                {text(
                  'izolovaný, auditovatelný konektor',
                  'izolovaný, auditovateľný konektor',
                )}
              </dd>
            </div>
            <div>
              <dt>{text('Platby', 'Platby')}</dt>
              <dd>
                {text(
                  'PSP a tok plateb čekají na schválení',
                  'PSP a tok platieb čakajú na schválenie',
                )}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="trust-bar">
        <div className="page-shell trust-bar__grid">
          <div>
            <Route />
            <strong>
              {text('Každé město zvlášť', 'Každé mesto samostatne')}
            </strong>
            <span>
              {text(
                'samostatný launch gate',
                'samostatné schválenie spustenia',
              )}
            </span>
          </div>
          <div>
            <KeyRound />
            <strong>
              {text('Idempotentní příkazy', 'Idempotentné príkazy')}
            </strong>
            <span>
              {text(
                'bez duplicitního oprávnění',
                'bez duplicitného oprávnenia',
              )}
            </span>
          </div>
          <div>
            <Database />
            <strong>{text('Reconciliation', 'Párovanie transakcií')}</strong>
            <span>
              {text('transakce až k vypořádání', 'transakcie až po zúčtovanie')}
            </span>
          </div>
          <div>
            <Headphones />
            <strong>{text('Provozní podpora', 'Prevádzková podpora')}</strong>
            <span>
              {text('jasná eskalační cesta', 'jasná eskalačná cesta')}
            </span>
          </div>
        </div>
      </section>

      <section className="requirements-section page-shell" id="podklady">
        <div className="section-heading section-heading--wide">
          <span className="eyebrow">
            {text('Vstupy k úvodnímu posouzení', 'Vstupy na úvodné posúdenie')}
          </span>
          <h2>
            {text(
              'Co potřebujeme od města nebo provozovatele.',
              'Čo potrebujeme od mesta alebo prevádzkovateľa.',
            )}
          </h2>
          <p>
            {text(
              'Na první schůzce nepotřebujeme hotový projekt. Potřebujeme identifikovat vlastníka parkovacího systému, smluvní cestu a technické možnosti.',
              'Na prvom stretnutí nepotrebujeme hotový projekt. Potrebujeme identifikovať vlastníka parkovacieho systému, zmluvnú cestu a technické možnosti.',
            )}
          </p>
        </div>
        <div className="requirements-grid">
          {integrationNeeds(text).map((group) => (
            <article className="requirement-card" key={group.title}>
              <span className="requirement-icon">{group.icon}</span>
              <h3>{group.title}</h3>
              <ul>
                {group.items.map((item) => (
                  <li key={item}>
                    <Check />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="process-section">
        <div className="page-shell">
          <div className="section-heading section-heading--wide">
            <span className="eyebrow">
              {text('Postup spolupráce', 'Postup spolupráce')}
            </span>
            <h2>
              {text(
                'Od discovery k bezpečnému spuštění.',
                'Od úvodnej analýzy k bezpečnému spusteniu.',
              )}
            </h2>
          </div>
          <ol className="process-list">
            {[
              [
                '01',
                text('Discovery', 'Úvodná analýza'),
                text(
                  'Provozovatel, systém, zóny, tarif a smluvní cesta.',
                  'Prevádzkovateľ, systém, zóny, tarifa a zmluvná cesta.',
                ),
              ],
              [
                '02',
                text('NDA a specifikace', 'NDA a špecifikácia'),
                text(
                  'Capability matrix, odpovědnosti a testovací scénáře.',
                  'Matica schopností, zodpovednosti a testovacie scenáre.',
                ),
              ],
              [
                '03',
                text('Sandbox', 'Sandbox'),
                text(
                  'Adaptér, tarifní testy, idempotence a chybové stavy.',
                  'Adaptér, tarifné testy, idempotencia a chybové stavy.',
                ),
              ],
              [
                '04',
                text('Certifikace', 'Certifikácia'),
                text(
                  'Funkční, finanční, bezpečnostní a provozní acceptance.',
                  'Funkčná, finančná, bezpečnostná a prevádzková akceptácia.',
                ),
              ],
              [
                '05',
                text('Omezený pilot', 'Obmedzený pilot'),
                text(
                  'Vybrané zóny nebo cohort, denní kontrola a nulová akvizice.',
                  'Vybrané zóny alebo skupina, denná kontrola a nulová akvizícia.',
                ),
              ],
              [
                '06',
                text('Launch', 'Spustenie'),
                text(
                  'Monitoring, support, reconciliation a společný go-live.',
                  'Monitoring, podpora, párovanie a spoločné spustenie.',
                ),
              ],
            ].map(([number, title, text]) => (
              <li key={number}>
                <span>{number}</span>
                <div>
                  <strong>{title}</strong>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="readiness-section page-shell">
        <div className="readiness-card readiness-card--ready">
          <span className="eyebrow">
            {text('K úvodnímu jednání', 'Na úvodné rokovanie')}
          </span>
          <h2>
            {text(
              'Podklady, které máme připravené',
              'Podklady, ktoré máme pripravené',
            )}
          </h2>
          <ul>
            <li>
              <FileCheck2 />{' '}
              {text(
                'Produktový a technický one-pager',
                'Produktový a technický prehľad',
              )}
            </li>
            <li>
              <FileCheck2 />{' '}
              {text(
                'Architektura klient · platforma · city adaptér',
                'Architektúra klient · platforma · mestský adaptér',
              )}
            </li>
            <li>
              <FileCheck2 />{' '}
              {text(
                'Capability matrix a integrační checklist',
                'Matica schopností a integračný kontrolný zoznam',
              )}
            </li>
            <li>
              <FileCheck2 />{' '}
              {text(
                'Funkční a failure test scénáře',
                'Funkčné a chybové testovacie scenáre',
              )}
            </li>
            <li>
              <FileCheck2 />{' '}
              {text(
                'Návrh pilotu a provozních metrik',
                'Návrh pilotu a prevádzkových metrík',
              )}
            </li>
          </ul>
        </div>
        <div className="readiness-card readiness-card--gate">
          <span className="eyebrow">
            {text(
              'Před produkčním podpisem',
              'Pred podpisom zmluvy na ostrú prevádzku',
            )}
          </span>
          <h2>
            {text(
              'Povinné firemní a regulační brány',
              'Povinné firemné a regulačné požiadavky',
            )}
          </h2>
          <ul>
            <li>
              <CircleAlert />{' '}
              {text(
                'Doklady právnické osoby a podpisová oprávnění',
                'Doklady právnickej osoby a podpisové oprávnenia',
              )}
            </li>
            <li>
              <CircleAlert />{' '}
              {text(
                'Finální PSP a právní režim toku peněz',
                'Finálny PSP a právny režim toku peňazí',
              )}
            </li>
            <li>
              <CircleAlert />{' '}
              {text(
                'DPA, penetrační test a pojištění',
                'DPA, penetračný test a poistenie',
              )}
            </li>
            <li>
              <CircleAlert />{' '}
              {text(
                'Produkční HA, on-call a finanční jistota',
                'Produkčné HA, on-call a finančná zábezpeka',
              )}
            </li>
          </ul>
          <p>
            {text(
              'Tyto body neprezentujeme jako splněné dříve, než jsou doložené.',
              'Tieto body neprezentujeme ako splnené, kým nie sú doložené.',
            )}
          </p>
        </div>
      </section>

      <ContactBand />
    </main>
  );
}

function CoveragePage() {
  const { locale, text } = usePublicLocale();
  const czechCities = [
    'Praha',
    'Brno',
    'Ostrava',
    'Plzeň',
    'Liberec',
    'Olomouc',
    'Jihlava',
    'Pardubice',
    'Karlovy Vary',
    'Kladno',
    'České Budějovice',
    'Zlín',
    'Frýdek-Místek',
    'Ústí nad Labem',
    'Hradec Králové',
    'Teplice',
    'Opava',
    'Jičín',
    'Mladá Boleslav',
    'Kolín',
    'Říčany',
    'Mělník',
  ];
  const slovakCities = [
    'Bratislava',
    'Košice',
    'Žilina',
    'Banská Bystrica',
    'Trnava',
    'Nitra',
    'Prešov',
    'Trenčín',
    'Martin',
  ];
  const markets = [
    {
      code: 'CZ',
      name: text('Česko', 'Česko'),
      cities: czechCities,
      icon: <Landmark />,
    },
    {
      code: 'SK',
      name: text('Slovensko', 'Slovensko'),
      cities: slovakCities,
      icon: <Building2 />,
    },
    {
      code: 'DE',
      name: text('Německo', 'Nemecko'),
      cities: ['Berlin', 'Düsseldorf', 'Erlangen'],
      icon: <Landmark />,
    },
    {
      code: 'ES',
      name: text('Španělsko', 'Španielsko'),
      cities: ['Madrid', 'València', 'Barcelona'],
      icon: <Landmark />,
    },
    {
      code: 'IT',
      name: text('Itálie', 'Taliansko'),
      cities: ['Milano', 'Roma', 'Torino'],
      icon: <Landmark />,
    },
    {
      code: 'PL',
      name: text('Polsko', 'Poľsko'),
      cities: ['Poznań', 'Kraków', 'Gdańsk'],
      icon: <Landmark />,
    },
  ];
  const preferredCountry = locale === 'cs' ? 'CZ' : locale.toUpperCase();
  markets.sort(
    (a, b) =>
      Number(b.code === preferredCountry) - Number(a.code === preferredCountry),
  );
  return (
    <main className="content-page">
      <section className="coverage-hero page-shell">
        <span className="eyebrow">{text('Pokrytí', 'Pokrytie')}</span>
        <h1>
          {text('Města zapojujeme po jednom.', 'Mestá pripájame po jednom.')}
        </h1>
        <p>
          {text(
            'Platby přes Parknuto zatím nejsou dostupné v žádném městě. Spuštění vždy vyžaduje smlouvu, technické ověření a schválené platby.',
            'Platby cez Parknuto zatiaľ nie sú dostupné v žiadnom meste. Spustenie vždy vyžaduje zmluvu, technické overenie a schválené platby.',
          )}
        </p>
      </section>
      <section className="coverage-cities page-shell">
        <div className="coverage-cities__intro">
          <span className="eyebrow">
            {text('Města v přípravě', 'Mestá v príprave')}
          </span>
          <h2>
            {text(
              'Průběžně rozšiřujeme náš záběr.',
              'Priebežne rozširujeme náš záber.',
            )}
          </h2>
          <p>
            {text(
              'Konkrétní město může být ve fázi průzkumu, komunikace nebo technické přípravy. Uvedení v seznamu neznamená spuštěnou službu ani uzavřenou spolupráci.',
              'Konkrétne mesto môže byť vo fáze prieskumu, komunikácie alebo technickej prípravy. Uvedenie v zozname neznamená spustenú službu ani uzatvorenú spoluprácu.',
            )}
          </p>
        </div>
        <div className="coverage-market-grid">
          {markets.map((market) => (
            <article className="coverage-market" key={market.code}>
              <header className="coverage-market__header">
                <span className="city-icon">{market.icon}</span>
                <div>
                  <span className="coverage-market__code">{market.code}</span>
                  <h3>{market.name}</h3>
                </div>
                <span className="status-tag">
                  {text('V přípravě', 'V príprave')}
                </span>
              </header>
              <ul className="coverage-city-list">
                {market.cities.map((city) => (
                  <li key={city}>{city}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
      <section className="coverage-principles page-shell">
        <h2>
          {text('Co znamená „spuštěné město“', 'Čo znamená „spustené mesto“')}
        </h2>
        <div>
          <FeatureCard
            icon={<FileCheck2 />}
            title={text('Smluvně potvrzené', 'Zmluvne potvrdené')}
            text={text(
              'Rozsah, ceny, podpora, data a odpovědnosti jsou písemně schválené.',
              'Rozsah, ceny, podpora, dáta a zodpovednosti sú písomne schválené.',
            )}
          />
          <FeatureCard
            icon={<ServerCog />}
            title={text('Technicky certifikované', 'Technicky certifikované')}
            text={text(
              'Sandbox, tarif, příkazy, enforcement a výpadkové scénáře prošly testem.',
              'Sandbox, tarifa, príkazy, kontrola oprávnení a výpadkové scenáre prešli testom.',
            )}
          />
          <FeatureCard
            icon={<ReceiptText />}
            title={text('Finančně vypořádané', 'Finančne vysporiadané')}
            text={text(
              'Platba, doklad, refund, reklamace a reconciliation mají jednoznačná pravidla.',
              'Platba, doklad, refundácia, reklamácia a párovanie majú jednoznačné pravidlá.',
            )}
          />
        </div>
      </section>
      <ContactBand />
    </main>
  );
}

function LegalPage({ kind }: { kind: 'privacy' | 'terms' }) {
  const { text } = usePublicLocale();
  const privacy = kind === 'privacy';
  return (
    <main className="legal-page page-shell">
      <span className="eyebrow">
        {text('Informace k veřejnému pilotu', 'Informácie k verejnému pilotu')}
      </span>
      <h1>
        {privacy
          ? text('Ochrana soukromí', 'Ochrana súkromia')
          : text('Podmínky používání', 'Podmienky používania')}
      </h1>
      <p className="legal-lead">
        {text(
          'Platnost od 7. srpna 2026 · Parknuto je ve veřejné pilotní fázi a zatím neprodává parkovací oprávnění.',
          'Platnosť od 7. augusta 2026 · Parknuto je vo verejnej pilotnej fáze a zatiaľ nepredáva parkovacie oprávnenia.',
        )}
      </p>
      {privacy ? (
        <div className="legal-content">
          <h2>{text('Kdo web provozuje', 'Kto prevádzkuje web')}</h2>
          <p>
            {text(
              'Správcem osobních údajů a provozovatelem projektu Parknuto je',
              'Prevádzkovateľom osobných údajov a projektu Parknuto je',
            )}{' '}
            <strong>{OPERATOR_NAME}</strong>, {text('IČO', 'IČO')} {OPERATOR_ID}
            , {text('se sídlem', 'so sídlom')} {OPERATOR_ADDRESS}.{' '}
            {text(
              'Kontaktní osobou je Filip Decastelo. Pro otázky k soukromí napište na',
              'Kontaktnou osobou je Filip Decastelo. Otázky k súkromiu posielajte na',
            )}{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
          <h2>{text('Mapa a poloha', 'Mapa a poloha')}</h2>
          <p>
            {text(
              'Poloha se použije pouze po vašem souhlasu pro zobrazení mapy a vyhledání zóny. Souřadnice zpracovává prohlížeč a veřejné zónové rozhraní Geoportálu Praha. Parknuto je v tomto pilotu neukládá do uživatelského profilu.',
              'Poloha sa použije iba po vašom súhlase na zobrazenie mapy a vyhľadanie zóny. Súradnice spracúva prehliadač a verejné zónové rozhranie Geoportálu Praha. Parknuto ich v tomto pilote neukladá do používateľského profilu.',
            )}
          </p>
          <h2>{text('SPZ a parkovací údaje', 'EČV a údaje o parkovaní')}</h2>
          <p>
            {text(
              'Veřejná ukázka nevyžaduje SPZ, délku parkování ani platební údaje. Parkovací oprávnění zde nekoupíte.',
              'Verejná ukážka nevyžaduje EČV, dĺžku parkovania ani platobné údaje. Parkovacie oprávnenie tu nekúpite.',
            )}
          </p>
          <h2>{text('Komunikace e-mailem', 'E-mailová komunikácia')}</h2>
          <p>
            {text(
              'Pokud nám napíšete, zpracujeme adresu, obsah zprávy a podpis pro vyřízení požadavku a navazující komunikaci. Zprávy uchováme po dobu potřebnou pro jednání a právní ochranu.',
              'Ak nám napíšete, spracujeme adresu, obsah správy a podpis na vybavenie požiadavky a nadväzujúcu komunikáciu. Správy uchováme počas obdobia potrebného na rokovanie a právnu ochranu.',
            )}
          </p>
          <h2>{text('Cookies a analytika', 'Súbory cookie a analytika')}</h2>
          <p>
            {text(
              'Po vašem výslovném souhlasu používáme Google Analytics 4 pro souhrnné měření návštěvnosti a používání jednotlivých obrazovek. Neposíláme SPZ, e-mail ani přesnou polohu a nepoužíváme analytická data k reklamní personalizaci. Volbu ukládáme v prohlížeči a můžete ji kdykoli změnit přes „Nastavení analytiky“ v patičce. Bez souhlasu se Google tag nenačte. Nezbytné technické logy hostingu mohou obsahovat IP adresu a údaje o požadavku pro bezpečnost a diagnostiku.',
              'Po vašom výslovnom súhlase používame Google Analytics 4 na súhrnné meranie návštevnosti a používania jednotlivých obrazoviek. Neposielame EČV, e-mail ani presnú polohu a analytické dáta nepoužívame na reklamnú personalizáciu. Voľbu ukladáme v prehliadači a môžete ju kedykoľvek zmeniť cez „Nastavenia analytiky“ v päte. Bez súhlasu sa Google tag nenačíta. Nevyhnutné technické logy hostingu môžu obsahovať IP adresu a údaje o požiadavke na účely bezpečnosti a diagnostiky.',
            )}
          </p>
          <h2>{text('Vaše práva', 'Vaše práva')}</h2>
          <p>
            {text(
              'Můžete požádat o přístup, opravu nebo výmaz údajů z e-mailové komunikace. Při pochybnostech se můžete obrátit také na Úřad pro ochranu osobních údajů.',
              'Môžete požiadať o prístup, opravu alebo vymazanie údajov z e-mailovej komunikácie. V prípade pochybností sa môžete obrátiť aj na Úrad na ochranu osobných údajov Slovenskej republiky.',
            )}
          </p>
        </div>
      ) : (
        <div className="legal-content">
          <h2>{text('Provozovatel', 'Prevádzkovateľ')}</h2>
          <p>
            {text(
              'Parknuto je produkt společnosti',
              'Parknuto je produkt spoločnosti',
            )}{' '}
            <strong>{OPERATOR_NAME}</strong>, {text('IČO', 'IČO')} {OPERATOR_ID}
            , {text('se sídlem', 'so sídlom')} {OPERATOR_ADDRESS}.
          </p>
          <h2>{text('Rozsah služby', 'Rozsah služby')}</h2>
          <p>
            {text(
              'Web zobrazuje produktový pilot, veřejná mapová data a stav připravovaných měst. Neumožňuje koupit parkovací oprávnění a nenahrazuje oficiální platební kanál města.',
              'Web zobrazuje produktový pilot, verejné mapové dáta a stav pripravovaných miest. Neumožňuje kúpiť parkovacie oprávnenie a nenahrádza oficiálny platobný kanál mesta.',
            )}
          </p>
          <h2>{text('Ověření parkovací zóny', 'Overenie parkovacej zóny')}</h2>
          <p>
            {text(
              'Mapová data mají informační charakter. Řidič musí vždy ověřit zónu, dobu zpoplatnění a místní pravidla podle dopravního značení a oficiálních informací provozovatele.',
              'Mapové dáta majú informačný charakter. Vodič si musí vždy overiť zónu, čas spoplatnenia a miestne pravidlá podľa dopravného značenia a oficiálnych informácií prevádzkovateľa.',
            )}
          </p>
          <h2>{text('Dostupnost a přesnost', 'Dostupnosť a presnosť')}</h2>
          <p>
            {text(
              'Veřejné zdroje nebo mapa mohou být dočasně nedostupné či opožděné. Parknuto proto v pilotu nepotvrzuje vznik parkovacího oprávnění.',
              'Verejné zdroje alebo mapa môžu byť dočasne nedostupné či oneskorené. Parknuto preto v pilote nepotvrdzuje vznik parkovacieho oprávnenia.',
            )}
          </p>
          <h2>
            {text(
              'Duševní vlastnictví a zdroje',
              'Duševné vlastníctvo a zdroje',
            )}
          </h2>
          <p>
            {text(
              'Značka a rozhraní Parknuto jsou chráněné jejich vlastníky. Mapový podklad používá OpenStreetMap a zónová vrstva veřejná data IPR Praha s uvedenou atribucí.',
              'Značka a rozhranie Parknuto sú chránené ich vlastníkmi. Mapový podklad používa OpenStreetMap a zónová vrstva verejné dáta IPR Praha s uvedenou atribúciou.',
            )}
          </p>
          <h2>{text('Kontakt', 'Kontakt')}</h2>
          <p>
            {text(
              'Dotazy k pilotu a spolupráci posílejte na',
              'Otázky k pilotu a spolupráci posielajte na',
            )}{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </div>
      )}
    </main>
  );
}

function ContactBand() {
  const { text } = usePublicLocale();
  return (
    <section className="contact-band">
      <div className="page-shell contact-band__inner">
        <span className="contact-icon">
          <Mail />
        </span>
        <div>
          <span className="eyebrow">
            {text('Kontakt pro napojení', 'Kontakt pre pripojenie')}
          </span>
          <h2>
            {text(
              'Začněme krátkým technicko-obchodním callem.',
              'Začnime krátkym technicko-obchodným rozhovorom.',
            )}
          </h2>
          <p>
            {text(
              'Pošlete město, provozovatele a stručný popis současného systému. Vrátíme konkrétní discovery checklist.',
              'Pošlite mesto, prevádzkovateľa a stručný opis súčasného systému. Odpovieme konkrétnym checklistom pre úvodnú analýzu.',
            )}
          </p>
        </div>
        <a
          className="primary-button"
          href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
            text(
              'Napojení města / provozovatele — Parknuto',
              'Pripojenie mesta / prevádzkovateľa — Parknuto',
            ),
          )}`}
        >
          {CONTACT_EMAIL} <ArrowRight />
        </a>
      </div>
    </section>
  );
}

function AnalyticsConsentBanner({
  open,
  currentConsent,
  onClose,
}: {
  open: boolean;
  currentConsent: AnalyticsConsent;
  onClose: (consent: Exclude<AnalyticsConsent, 'unknown'>) => void;
}) {
  const { text } = usePublicLocale();
  if (!open || !isAnalyticsConfigured()) return null;
  return (
    <aside
      className="analytics-consent"
      role="dialog"
      aria-modal="true"
      aria-labelledby="analytics-consent-title"
    >
      <div>
        <span className="eyebrow">
          {text('Vaše soukromí', 'Vaše súkromie')}
        </span>
        <h2 id="analytics-consent-title">
          {text('Pomozte zlepšit Parknuto', 'Pomôžte zlepšiť Parknuto')}
        </h2>
        <p>
          {text(
            'Anonymní analytika nám ukáže, které části webu fungují. Do Google Analytics neposíláme SPZ, e-mail ani přesnou polohu. Web funguje i bez souhlasu.',
            'Anonymná analytika nám ukáže, ktoré časti webu fungujú. Do Google Analytics neposielame EČV, e-mail ani presnú polohu. Web funguje aj bez súhlasu.',
          )}
        </p>
        {currentConsent !== 'unknown' && (
          <small>
            {text('Aktuálně', 'Aktuálne')}:{' '}
            {currentConsent === 'granted'
              ? text('povoleno', 'povolené')
              : text('zakázáno', 'zakázané')}
          </small>
        )}
      </div>
      <div className="analytics-consent__actions">
        <button
          className="secondary-button"
          type="button"
          onClick={() => onClose('denied')}
        >
          {text('Ne, děkuji', 'Nie, ďakujem')}
        </button>
        <button
          className="primary-button"
          type="button"
          onClick={() => onClose('granted')}
        >
          {text('Povolit analytiku', 'Povoliť analytiku')}
        </button>
      </div>
    </aside>
  );
}

function Footer({ onAnalyticsSettings }: { onAnalyticsSettings: () => void }) {
  const { locale, text } = usePublicLocale();
  const links = [
    ['partners', text('Pro města', 'Pre mestá')],
    ['coverage', text('Pokrytí', 'Pokrytie')],
    ['privacy', text('Soukromí', 'Súkromie')],
    ['terms', text('Podmínky', 'Podmienky')],
  ] as const;
  return (
    <footer className="site-footer">
      <div className="page-shell site-footer__inner">
        <div>
          <Brand compact />
          <p>
            {text(
              'Česká aplikace pro městské parkování. Veřejný pilot.',
              'Aplikácia pre mestské parkovanie. Verejný pilot.',
            )}
          </p>
        </div>
        <div className="footer-links">
          {links.map(([route, label]) => {
            const path = publicPath(route, locale);
            return (
              <a
                key={route}
                href={path}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(path);
                }}
              >
                {label}
              </a>
            );
          })}
          <a href={`mailto:${CONTACT_EMAIL}`}>{text('Kontakt', 'Kontakt')}</a>
          {isAnalyticsConfigured() && (
            <button
              className="footer-link-button"
              type="button"
              onClick={onAnalyticsSettings}
            >
              {text('Nastavení analytiky', 'Nastavenia analytiky')}
            </button>
          )}
        </div>
        <p className="copyright">
          © 2026 Parknuto · {text('produkt', 'produkt')} {OPERATOR_NAME},{' '}
          {text('IČO', 'IČO')} {OPERATOR_ID} ·{' '}
          {text('Datový podklad zón', 'Dátový podklad zón')} © IPR Praha ·{' '}
          {text('Mapová data', 'Mapové dáta')} © OpenStreetMap
        </p>
      </div>
    </footer>
  );
}

function NotFoundPage() {
  const { locale, text } = usePublicLocale();
  return (
    <main className="content-page page-shell">
      <h1>{text('Stránka se nenašla', 'Stránka sa nenašla')}</h1>
      <p>{text('Tato adresa neexistuje.', 'Táto adresa neexistuje.')}</p>
      <a
        href={publicPath('app', locale)}
        onClick={(event) => {
          event.preventDefault();
          navigate(publicPath('app', locale));
        }}
      >
        {text('Zpět na mapu zón', 'Späť na mapu zón')}
      </a>
    </main>
  );
}

export function App({
  initialPath = '/',
  initialLocale = 'cs',
}: { initialPath?: string; initialLocale?: PublicLocale } = {}) {
  const [locale, setLocale] = useState<PublicLocale>(() =>
    typeof window === 'undefined'
      ? initialLocale
      : detectPublicLocale({
          hostname: window.location.hostname,
          pathname: window.location.pathname,
          search: window.location.search,
        }),
  );
  const [route, setRoute] = useState<RouteName>(() =>
    routeFromPath(
      typeof window === 'undefined' ? initialPath : window.location.pathname,
      locale,
    ),
  );
  const [analyticsConsent, setAnalyticsConsentState] =
    useState<AnalyticsConsent>(getAnalyticsConsent);
  const [showAnalyticsSettings, setShowAnalyticsSettings] = useState(
    () => isAnalyticsConfigured() && analyticsConsent === 'unknown',
  );
  useEffect(() => {
    if (route !== 'cityops') initializeAnalytics();
  }, [route]);
  useEffect(() => {
    const update = () => {
      const nextLocale = detectPublicLocale({
        hostname: window.location.hostname,
        pathname: window.location.pathname,
        search: window.location.search,
      });
      setLocale(nextLocale);
      setRoute(routeFromPath(window.location.pathname, nextLocale));
    };
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);
  useEffect(() => {
    if (route === 'cityops' || route === 'notfound') return;
    trackPageView(
      window.location.pathname,
      getPageMetadata(route, locale).title,
    );
  }, [route, locale, analyticsConsent]);

  useEffect(() => {
    const setMeta = (
      attribute: 'name' | 'property',
      key: string,
      content: string,
    ) => {
      let meta = document.querySelector(`meta[${attribute}="${key}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, key);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    document.documentElement.lang = locale;
    document
      .querySelectorAll(
        'script[type="application/ld+json"], meta[property="og:locale:alternate"]',
      )
      .forEach((node) => node.remove());
    let manifest = document.querySelector('link[rel="manifest"]');
    if (!manifest) {
      manifest = document.createElement('link');
      manifest.setAttribute('rel', 'manifest');
      document.head.appendChild(manifest);
    }
    manifest.setAttribute('href', publicManifestPath(locale));
    if (route === 'cityops' || route === 'notfound') {
      document
        .querySelectorAll(
          'link[rel="canonical"], link[rel="alternate"][hreflang], meta[property="og:url"]',
        )
        .forEach((node) => node.remove());
      setMeta('name', 'robots', 'noindex');
      if (route === 'notfound') {
        const { title, description } = PUBLIC_ERROR_METADATA[locale];
        document.title = title;
        setMeta('name', 'description', description);
        setMeta('property', 'og:title', title);
        setMeta('property', 'og:description', description);
        setMeta('property', 'og:locale', PUBLIC_OG_LOCALES[locale]);
      } else {
        document.title = 'CityOps · Parknuto';
        document
          .querySelectorAll(
            'meta[name="description"], meta[property="og:title"], meta[property="og:description"], meta[property="og:locale"]',
          )
          .forEach((node) => node.remove());
      }
      return;
    }
    const { title, description } = getPageMetadata(route, locale);
    // Recover normal metadata when navigation starts on the prerendered 404 page.
    document.querySelector('meta[name="robots"][content="noindex"]')?.remove();
    for (const attributes of [
      { rel: 'canonical' },
      ...[
        ...PUBLIC_LOCALES,
        ...(PUBLISHED_PUBLIC_LOCALES.includes('en')
          ? ['x-default' as const]
          : []),
      ].map((language) => ({
        rel: 'alternate',
        hreflang: language,
      })),
    ]) {
      const selector = Object.entries(attributes)
        .map(([key, value]) => `[${key}="${value}"]`)
        .join('');
      if (!document.querySelector(`link${selector}`)) {
        const link = document.createElement('link');
        Object.entries(attributes).forEach(([key, value]) =>
          link.setAttribute(key, value),
        );
        document.head.appendChild(link);
      }
    }
    const routeForPublicPath = route;
    const canonical = `${PUBLIC_ORIGINS[locale]}${publicPath(
      routeForPublicPath,
      locale,
    )}`;

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:locale', PUBLIC_OG_LOCALES[locale]);
    if (route === 'app') {
      const structured = document.createElement('script');
      structured.type = 'application/ld+json';
      structured.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Parknuto',
        url: PUBLIC_ORIGINS[locale],
        inLanguage: locale,
      }).replace(/</g, '\\u003c');
      document.head.appendChild(structured);
    }
    PUBLISHED_PUBLIC_LOCALES.filter((language) => language !== locale).forEach(
      (language) => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:locale:alternate');
        meta.setAttribute('content', PUBLIC_OG_LOCALES[language]);
        document.head.appendChild(meta);
      },
    );
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute('href', canonical);
    PUBLISHED_PUBLIC_LOCALES.forEach((alternateLocale) => {
      document
        .querySelector(`link[rel="alternate"][hreflang="${alternateLocale}"]`)
        ?.setAttribute(
          'href',
          `${PUBLIC_ORIGINS[alternateLocale]}${publicPath(
            routeForPublicPath,
            alternateLocale,
          )}`,
        );
    });
    if (PUBLISHED_PUBLIC_LOCALES.includes('en')) {
      document
        .querySelector('link[rel="alternate"][hreflang="x-default"]')
        ?.setAttribute(
          'href',
          `${PUBLIC_ORIGINS.en}${publicPath(routeForPublicPath, 'en')}`,
        );
    }
  }, [locale, route]);

  const updateAnalyticsConsent = (
    consent: Exclude<AnalyticsConsent, 'unknown'>,
  ) => {
    setAnalyticsConsent(consent);
    setAnalyticsConsentState(consent);
    setShowAnalyticsSettings(false);
  };
  if (route === 'cityops')
    return (
      <Suspense fallback={<p>Načítám CityOps…</p>}>
        <CityOpsPage />
      </Suspense>
    );
  return (
    <LocaleContext.Provider value={locale}>
      <Header route={route} />
      {route === 'app' && <AppPage />}
      {route === 'partners' && <PartnersPage />}
      {route === 'coverage' && <CoveragePage />}
      {route === 'privacy' && <LegalPage kind="privacy" />}
      {route === 'terms' && <LegalPage kind="terms" />}
      {route === 'notfound' && <NotFoundPage />}
      <Footer onAnalyticsSettings={() => setShowAnalyticsSettings(true)} />
      <AnalyticsConsentBanner
        open={showAnalyticsSettings}
        currentConsent={analyticsConsent}
        onClose={updateAnalyticsConsent}
      />
    </LocaleContext.Provider>
  );
}
